/** Shared types for the DicomScrub engine (runs in a Web Worker and in tests). */

/** PS3.15 Annex E action codes. */
export type Action = "X" | "Z" | "D" | "U" | "K" | "C";

export interface ProfileOptions {
  /** Retain Longitudinal Temporal Information with Modified Dates (113107). Shift dates by dateShiftDays. */
  modifyDates: boolean;
  /** Days added to every date (negative allowed). Chosen once per batch. */
  dateShiftDays: number;
  /** Retain Patient Characteristics Option (113108): age, sex, size, weight, ethnic group, ... */
  retainPatientCharacteristics: boolean;
  /** Retain Device Identity Option (113109). */
  retainDeviceIdentity: boolean;
  /** Retain Institution Identity Option (113112). */
  retainInstitutionIdentity: boolean;
  /** Retain UIDs Option (113110). When false, all UIDs are consistently remapped. */
  retainUIDs: boolean;
  /** Retain Safe Private Option (113111). Simplified: keep ALL private tags. Default false = strip. */
  retainPrivateTags: boolean;
  /** Clean Descriptors Option (113105). Simplified: keep study/series description and protocol name. */
  keepDescriptors: boolean;
  /** Keep overlay planes (60xx,3000). */
  keepOverlays: boolean;
  /** Pseudonym scheme for PatientID / PatientName. */
  patientIdPrefix: string;
  /** Start index for pseudonyms. */
  patientIdStart: number;
  /** Extra user rules (Pro). Applied after the profile. */
  customRules: CustomRule[];
  /** Rectangles to black out in pixel data, keyed by original file index (Pro). */
  redactions: Record<number, RedactRect[]>;
}

export interface CustomRule {
  /** 8 hex chars, uppercase, e.g. "00081030". */
  tag: string;
  action: "keep" | "remove" | "empty" | "replace";
  value?: string;
}

export interface RedactRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface AuditEntry {
  tag: string;
  name: string;
  action: Action | "REDACT" | "ADD";
  before: string;
  after: string;
}

export interface FileSummary {
  index: number;
  name: string;
  size: number;
  ok: boolean;
  error?: string;
  sopClass?: string;
  modality?: string;
  transferSyntax?: string;
  patientName?: string;
  patientId?: string;
  studyDate?: string;
  studyDescription?: string;
  rows?: number;
  columns?: number;
  frames?: number;
  previewable: boolean;
}

export interface PreviewBitmap {
  width: number;
  height: number;
  /** RGBA, width*height*4 */
  rgba: Uint8ClampedArray;
}

export interface ProcessedFile {
  index: number;
  name: string;
  outputName: string;
  ok: boolean;
  error?: string;
  bytes?: ArrayBuffer;
  audit: AuditEntry[];
}

export interface BatchResult {
  files: ProcessedFile[];
  uidMap: Record<string, string>;
  patientMap: Record<string, string>;
  dateShiftDays: number;
  options: ProfileOptions;
}

export const DEFAULT_OPTIONS: ProfileOptions = {
  modifyDates: true,
  dateShiftDays: 0,
  retainPatientCharacteristics: false,
  retainDeviceIdentity: false,
  retainInstitutionIdentity: false,
  retainUIDs: false,
  retainPrivateTags: false,
  keepDescriptors: false,
  keepOverlays: false,
  patientIdPrefix: "ANON",
  patientIdStart: 1,
  customRules: [],
  redactions: {},
};
